import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faSave,
    faTag,
    faInfoCircle,
    faMoneyBillWave,
    faClock,
    faMapMarkerAlt,
    faGraduationCap,
    faStar,
    faLink,
    faChalkboardTeacher,
    faUserShield
} from '@fortawesome/free-solid-svg-icons';
import Loader from '../Shared/Loader';
import Select from 'react-select';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FileUploader from '../Shared/FileUploader';

const AddAdsViewer = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [teachers, setTeachers] = useState([]);
    const [preview, setPreview] = useState(null);

    const fetchTeachers = async () => {
        try {
            const res = await ApiServices.get('Lookup/Teachers');
            if (res && Array.isArray(res)) {
                setTeachers(res.map(t => ({ value: t.id, label: t.name })));
            }
        } catch (e) { }
    };

    useEffect(() => {
        fetchTeachers();
    }, []);

    const formik = useFormik({
        initialValues: {
            Title: '',
            Description: '',
            Price: '',
            OriginalPrice: '',
            Discount: '',
            Duration: '',
            Location: '',
            Students: '',
            Rating: '',
            Link: '',
            TeacherId: null,
            IsPlatformOwner: false,
            Image: null
        },
        validationSchema: Yup.object({
            Title: Yup.string()
                .required('يرجى إدخال عنوان الإعلان')
                .min(5, 'العنوان يجب أن يكون 5 أحرف على الأقل'),
            Description: Yup.string()
                .required('يرجى إدخال وصف الإعلان')
                .min(10, 'الوصف يجب أن يكون 10 أحرف على الأقل'),
            Link: Yup.string()
                .url('يرجى إدخال رابط صحيح (يبدأ بـ http:// أو https://)')
                .nullable(),
            Price: Yup.number().typeError('يجب أن يكون رقماً'),
            OriginalPrice: Yup.number().typeError('يجب أن يكون رقماً'),
            Discount: Yup.number().typeError('يجب أن يكون رقماً').max(100, 'الخصم لا يمكن أن يتجاوز 100%'),
            Duration: Yup.number().typeError('يجب أن يكون رقماً'),
            Students: Yup.number().typeError('يجب أن يكون رقماً'),
            Rating: Yup.number().typeError('يجب أن يكون رقماً').min(0).max(5, 'التقييم من 0 إلى 5'),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const data = new FormData();
                data.append('Title', values.Title);
                data.append('Description', values.Description);
                data.append('Price', Number(values.Price) || 0);
                data.append('OriginalPrice', Number(values.OriginalPrice) || 0);
                data.append('Discount', Number(values.Discount) || 0);
                data.append('Duration', Number(values.Duration) || 0);
                data.append('Location', values.Location || '');
                data.append('Students', Number(values.Students) || 0);
                data.append('Rating', Number(values.Rating) || 0);
                data.append('Link', values.Link || '');
                data.append('IsPlatformOwner', values.IsPlatformOwner ? 'true' : 'false');

                if (values.TeacherId) {
                    data.append('TeacherId', values.TeacherId.value);
                }

                if (values.Image) {
                    data.append('Image', values.Image);
                }

                const res = await ApiServices.post('AdsViewer/create', data, true, 800000);
                if (res && res.check) {
                    toastHelper.success('تم إنشاء الإعلان بنجاح');
                    navigate(-1);
                } else {
                    toastHelper.error(res.message || 'فشل في حفظ الإعلان');
                }
            } catch (error) {
                toastHelper.error(error.message || 'حدث خطأ أثناء الاتصال بالسيرفر');
            } finally {
                setLoading(false);
            }
        }
    });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue('Image', file);
            setPreview(URL.createObjectURL(file));
        }
    };

    return (
        <div className="add-adsview-page">
            {loading && <Loader text="جاري الحفظ..." />}
            <div className="form-container">
                <header className="form-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <h1>إضافة إعلان جديد</h1>
                </header>

                <form onSubmit={formik.handleSubmit}>
                    <div className="form-grid">
                        <div className="input-group full-width platform-toggle-box">
                            <label className="checkbox-toggle">
                                <div className="label-info">
                                    <FontAwesomeIcon icon={faUserShield} />
                                    <span>هل هذا الإعلان خاص بالمنصة (Platform Owner)؟</span>
                                </div>
                                <input
                                    type="checkbox"
                                    name="IsPlatformOwner"
                                    checked={formik.values.IsPlatformOwner}
                                    onChange={formik.handleChange}
                                />
                            </label>
                        </div>

                        <div className="input-group full-width">
                            <label><FontAwesomeIcon icon={faTag} /> عنوان الإعلان *</label>
                            <input
                                type="text"
                                name="Title"
                                value={formik.values.Title}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="مثلاً: خصم خاص على كورس الرياضيات"
                                className={formik.touched.Title && formik.errors.Title ? 'input-error' : ''}
                            />
                            {formik.touched.Title && formik.errors.Title && <span className="error-txt">{formik.errors.Title}</span>}
                        </div>

                        <div className="input-group full-width">
                            <label><FontAwesomeIcon icon={faInfoCircle} /> الوصف *</label>
                            <textarea
                                name="Description"
                                value={formik.values.Description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="اكتب تفاصيل الإعلان هنا..."
                                rows="3"
                                className={formik.touched.Description && formik.errors.Description ? 'input-error' : ''}
                            />
                            {formik.touched.Description && formik.errors.Description && <span className="error-txt">{formik.errors.Description}</span>}
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faMoneyBillWave} /> السعر الحالي</label>
                            <input
                                type="number"
                                name="Price"
                                value={formik.values.Price}
                                onChange={formik.handleChange}
                                placeholder="0.00"
                                disabled={formik.values.IsPlatformOwner}
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faMoneyBillWave} /> السعر الأصلي (اختياري)</label>
                            <input
                                type="number"
                                name="OriginalPrice"
                                value={formik.values.OriginalPrice}
                                onChange={formik.handleChange}
                                placeholder="0.00"
                                disabled={formik.values.IsPlatformOwner}
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faClock} /> المدة (بالأيام)</label>
                            <input
                                type="number"
                                name="Duration"
                                value={formik.values.Duration}
                                onChange={formik.handleChange}
                                placeholder="مثلاً: 30"
                                disabled={formik.values.IsPlatformOwner}
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faMapMarkerAlt} /> الموقع الجغرافي</label>
                            <input
                                type="text"
                                name="Location"
                                value={formik.values.Location}
                                onChange={formik.handleChange}
                                placeholder="مثلاً: القاهرة"
                                disabled={formik.values.IsPlatformOwner}
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faChalkboardTeacher} /> المعلم المرتبط</label>
                            <Select
                                options={teachers}
                                value={formik.values.TeacherId}
                                onChange={(opt) => formik.setFieldValue('TeacherId', opt)}
                                placeholder="اختر المعلم..."
                                isRtl={true}
                                isDisabled={formik.values.IsPlatformOwner}
                                styles={{
                                    control: (base) => ({ ...base, borderRadius: '12px', padding: '5px', border: '2px solid #e2e8f0' })
                                }}
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faLink} /> الرابط (Link)</label>
                            <input
                                type="text"
                                name="Link"
                                value={formik.values.Link}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="https://..."
                                className={formik.touched.Link && formik.errors.Link ? 'input-error' : ''}
                            />
                            {formik.touched.Link && formik.errors.Link && <span className="error-txt">{formik.errors.Link}</span>}
                        </div>

                        <div className="input-group full-width">
                            <label><FontAwesomeIcon icon={faTag} /> ميديا الإعلان (صور أو فيديو)</label>
                            <FileUploader
                                id="ads-media"
                                accept="image/*,video/*"
                                MaxFileSize={50}
                                previewUrl={preview}
                                isVideo={formik.values.Image?.type?.includes('video')}
                                onFileChange={handleFileChange}
                                placeholder="اسحب وأفلت الميديا هنا"
                                subText="يمكنك رفع صور أو فيديوهات حتى 50 ميجابايت"
                            />
                        </div>

                        <div className="input-group stats-box">
                            <div className="stats-grid-form">
                                <div className="stat-field">
                                    <label><FontAwesomeIcon icon={faGraduationCap} /> الطلاب</label>
                                    <input type="number" name="Students" value={formik.values.Students} onChange={formik.handleChange} placeholder="0" />
                                </div>
                                <div className="stat-field">
                                    <label><FontAwesomeIcon icon={faStar} /> التقييم</label>
                                    <input type="number" name="Rating" value={formik.values.Rating} onChange={formik.handleChange} placeholder="0-5" min="0" max="5" step="0.5" />
                                </div>
                                <div className="stat-field">
                                    <label><FontAwesomeIcon icon={faTag} /> الخصم %</label>
                                    <input type="number" name="Discount" value={formik.values.Discount} onChange={formik.handleChange} placeholder="%" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <footer className="form-footer">
                        <button type="submit" className="submit-btn" disabled={loading}>
                            <FontAwesomeIcon icon={faSave} />
                            <span>حفظ الإعلان</span>
                        </button>
                    </footer>
                </form>
            </div>

            <style>{`
                .add-adsview-page { padding: 40px 20px; background: #f8fafc; min-height: 100vh; direction: rtl;   font-family: "Zain", sans-serif; }
                .form-container { max-width: 900px; margin: 0 auto; background: white; padding: 40px; border-radius: 24px; box-shadow: 0 10px 40px rgba(0,0,0,0.05); }
                .form-header { display: flex; align-items: center; gap: 20px; margin-bottom: 40px; border-bottom: 2px solid #f1f5f9; padding-bottom: 20px; }
                .back-btn { background: #f1f5f9; border: none; padding: 10px 20px; border-radius: 12px; cursor: pointer; color: #64748b; font-weight: 700; display: flex; align-items: center; gap: 10px; }
                .form-header h1 { margin: 0; font-size: 1.8rem; color: #1e293b; font-weight: 800; }

                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
                .full-width { grid-column: 1 / -1; }

                .input-group label { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-weight: 700; color: #475569; font-size: 0.95rem; }
                .input-group input[type="text"], .input-group input[type="number"], .input-group textarea {
                    width: 100%; padding: 14px 18px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 1rem;   font-family: "Zain", sans-serif; outline: none; transition: border-color 0.2s;
                }
                .input-group input:focus, .input-group textarea:focus { border-color: #6366f1; }
                .input-group input:disabled { background: #f8fafc; cursor: not-allowed; }
                .input-group .input-error { border-color: #ef4444; }
                .error-txt { color: #ef4444; font-size: 0.8rem; margin-top: 5px; display: block; font-weight: 600; }

                .platform-toggle-box { background: #f0f7ff; padding: 20px; border-radius: 15px; border: 2px dashed #6366f1; }
                .checkbox-toggle { display: flex; justify-content: space-between; align-items: center; width: 100%; cursor: pointer; }
                .checkbox-toggle .label-info { display: flex; align-items: center; gap: 12px; color: #1e40af; font-weight: 800; }
                .checkbox-toggle input { width: 22px; height: 22px; cursor: pointer; }

                .stats-grid-form { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; }
                
                .form-footer { margin-top: 40px; padding-top: 30px; border-top: 2px solid #f1f5f9; display: flex; justify-content: center; }
                .submit-btn { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); color: white; border: none; padding: 16px 60px; border-radius: 15px; font-size: 1.1rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 12px; transition: all 0.3s; box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4); }
                .submit-btn:hover { box-shadow: 0 15px 30px -5px rgba(99, 102, 241, 0.5); transform: translateY(-2px); }

                @media (max-width: 768px) {
                    .add-adsview-page { padding: 20px 10px; }
                    .form-container { padding: 20px; border-radius: 15px; }
                    .form-header { gap: 10px; margin-bottom: 25px; }
                    .form-header h1 { font-size: 1.4rem; }
                    .back-btn { padding: 8px 15px; font-size: 0.9rem; }
                    .form-grid { grid-template-columns: 1fr; gap: 20px; }
                    .stats-grid-form { grid-template-columns: 1fr; }
                    .submit-btn { width: 100%; padding: 14px 20px; }
                    .checkbox-toggle .label-info span { font-size: 0.85rem; }
                }
            `}</style>
        </div>
    );
};

export default AddAdsViewer;
